import store from '@/store';
import communicationSdkService from '@/modules/common/services/communicationSdkService';
import handleNavigation from '@/utils/navigationUtils';
import outgoingPostMessageUtils from '@/utils/eventCommunicationUtils/outgoingPostMessageUtils';

function getJWKSHandler(appCodeName) {
  return communicationSdkService.getJWKS(appCodeName);
}

function getUserHandler() {
  return {
    brandHost: window.location.origin,
    businessUid: store.getters['AuthStore/businessUid'],
    staffUid: store.getters['StaffStore/staffUid'],
    impersonate: store.getters['AuthStore/isImpersonating'],
    language: store.getters['BusinessStore/locale'],
  };
}

/**
 * Open a modal, it could be an Angular/Frontage-vue modal or a POV modal
 * @param modalName - the name of the modal to open, as defined in the modal store
 * @param modalProps - the props to pass to the modal, either as Array or Object
 */
const openModal = ({ modalName, modalProps }) => {
  let internalName = modalName;
  let enhancedModalProps = modalProps;

  switch (modalName) {
    case 'AppMarket':
      internalName = 'AppMarketWizard';
      // App market first params are $scope and $state, so this is to prevent exceptions
      enhancedModalProps = [{}, {}];
      if (modalProps && Array.isArray(modalProps)) {
        enhancedModalProps = [...enhancedModalProps, ...modalProps];
      } else if (modalProps && modalProps.appCodeName) {
        enhancedModalProps = [...enhancedModalProps, modalProps.appCodeName];
      }
      break;
    case 'PaymentWizard':
      enhancedModalProps = [{
        navigateTo: modalProps.navigateTo || '',
        source: modalProps.source || 'campaign',
        ...modalProps,
      }];
      break;
    case 'NewAppointment':
      // NewAppointment expects specific parameters in this order:
      // $targetEvent, client, conversation_uid, presetTimes, staffUid, notes, service_uid, analytics_properties
      enhancedModalProps = [null]; // $targetEvent is null
      if (modalProps && Array.isArray(modalProps)) {
        enhancedModalProps = [...enhancedModalProps, ...modalProps];
      } else if (modalProps && modalProps.client) {
        // If modalProps is an object, extract the expected parameters in the correct order
        enhancedModalProps = [
          null, // $targetEvent
          modalProps.client,
          modalProps.conversation_uid || null,
          modalProps.presetTimes || null,
          modalProps.staffUid || null,
          modalProps.notes || null,
          modalProps.service_uid || null,
          modalProps.analytics_properties || {},
          modalProps.ai_recommendation || null,
        ];
      }
      break;
    case 'NewEstimate':
      // NewEstimate expects specific parameters in this order:
      // $targetEvent, client, conversation_id, payment, item, service_name, copy_uid, analytics_properties
      enhancedModalProps = [null]; // $targetEvent is null
      if (modalProps && Array.isArray(modalProps)) {
        enhancedModalProps = [...enhancedModalProps, ...modalProps];
      } else if (modalProps && modalProps.client) {
        // If modalProps is an object, extract the expected parameters in the correct order
        enhancedModalProps = [
          null, // $targetEvent
          modalProps.client,
          modalProps.conversation_id || null,
          modalProps.payment || null,
          modalProps.item || null,
          modalProps.service_name || null,
          modalProps.copy_uid || null,
          modalProps.analytics_properties || {},
        ];
      }
      break;
    case 'IframeModal':
      internalName = 'iframe-modal';
      break;
    default:
      break;
  }
  store.dispatch('ModalStore/openModal', {
    name: internalName,
    payload: enhancedModalProps,
  });
};

export function startCommunicationWithSdk({ appDomain, iframeId, appId, appCodeName }) {
  const channel = new MessageChannel();
  const frontageChannel = channel.port1;
  const externalAppChannel = channel.port2;

  function iframeMessageChannelCreator(event) {
    if (event.origin !== appDomain) return;
    if (event.data.widgetUid !== appId && event.data.widgetUid !== appCodeName) {
      window.frames[iframeId].contentWindow.postMessage('InitWidgetErrorEvent', appDomain);
      return;
    }
    if (event.data.type === 'ReadyWidgetEvent') {
      window.frames[iframeId].contentWindow.postMessage('InitWidgetEvent', appDomain, [externalAppChannel]);
      // Remove the event listener after the channel is created
      window.removeEventListener('message', iframeMessageChannelCreator);
    }
  }
  window.addEventListener('message', iframeMessageChannelCreator);

  return frontageChannel;
}

/*
* @param {string} path - The path to navigate to can be relative or absolute
* @param {object} queryParams - The query params to be added to the url
* @param {string} type - The type of navigation, can be relative or external
* @param {boolean} newTab - A flag to open the link in a new tab
* */
async function navigate({
  path, route, queryParams, type, newTab = false,
}) {
  // We not just pass the param to the util because this function can be used as
  // 3rd party apps, and it can cause security issue to pass it as is
  if (type === 'relative') {
    handleNavigation({ path, route, queryParams });
  } else if (['external', 'absolute'].includes(type)) {
    window.open(path, newTab ? '_blank' : '_self');
  }
}

const openQuickAction = ({ actionName, quickActionProps, origin }) => {
  store.dispatch('ModalStore/setForceModalMode', true);
  outgoingPostMessageUtils.post('fire-quick-action', {
    actionName,
    origin,
    ...quickActionProps,
  });
};

const actions = {
  GetJWKSToken: getJWKSHandler,
  GetUser: getUserHandler,
  OpenModal: openModal,
  Navigate: navigate,
  OpenQuickAction: openQuickAction,
};

function communicationSdk(action, params) {
  if (actions[action]) {
    return actions[action](params);
  }
  console.error(`No handler was found for action "${action}"`);
  return {
    error: 'Invalid action',
  };
}

export default communicationSdk;
